import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Film, Smartphone, Crown, Megaphone, Play, Plus, Trash2, Clock, CheckCircle2, AlertCircle, ChevronLeft } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';

const TYPE_ICON = {
  product_showcase: Video,
  instagram_reel: Smartphone,
  social_video: Play,
  luxury_film: Crown,
  promotional: Megaphone,
};

const TYPE_COLOR = {
  product_showcase: 'bg-blue-50 text-blue-700',
  instagram_reel: 'bg-pink-50 text-pink-700',
  social_video: 'bg-green-50 text-green-700',
  luxury_film: 'bg-purple-50 text-purple-700',
  promotional: 'bg-orange-50 text-orange-700',
};

const STATUS_BADGE = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: Clock },
  strategy: { label: 'Strategy', color: 'bg-blue-100 text-blue-600', icon: Clock },
  scripted: { label: 'Scripted', color: 'bg-indigo-100 text-indigo-600', icon: Clock },
  planned: { label: 'Planned', color: 'bg-purple-100 text-purple-600', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-600', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-600', icon: AlertCircle },
};

export const VideoLibraryScreen = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/video-projects');
      setProjects(res.data.projects || []);
    } catch { toast.error('Failed to load projects'); }
    setLoading(false);
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this video project?')) return;
    try {
      await api.delete(`/video-projects/${id}`);
      setProjects(p => p.filter(x => x.id !== id));
      toast.success('Project deleted');
    } catch { toast.error('Delete failed'); }
  };

  const filtered = filter === 'all' ? projects : projects.filter(p => p.video_type === filter);

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="back-btn"><ChevronLeft size={24} /></button>
          <div className="flex-1"><h1 className="text-base font-bold" data-testid="video-library-title">Video Library</h1></div>
        </div>

        {/* Filter Chips */}
        <div className="px-4 pt-3 flex gap-2 overflow-x-auto pb-2">
          {[{ id: 'all', label: 'All' }, { id: 'product_showcase', label: 'Showcase' }, { id: 'instagram_reel', label: 'Reels' }, { id: 'luxury_film', label: 'Luxury' }, { id: 'promotional', label: 'Promo' }, { id: 'social_video', label: 'Social' }].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              data-testid={`filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-center py-12"><p className="text-sm text-gray-500">Loading...</p></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-state">
              <Film size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="text-base font-bold text-gray-600">No Videos Yet</p>
              <p className="text-xs text-gray-400 mt-1 max-w-[250px] mx-auto">Turn your products into engaging videos and reels using AI.</p>
              <button onClick={() => navigate('/my-products')}
                className="mt-4 px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-bold active:scale-95"
                data-testid="create-first-btn">
                <Plus size={14} className="inline mr-1" /> Create Your First Video
              </button>
            </div>
          ) : (
            filtered.map(p => {
              const Icon = TYPE_ICON[p.video_type] || Film;
              const color = TYPE_COLOR[p.video_type] || 'bg-gray-50 text-gray-700';
              const status = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
              const StatusIcon = status.icon;
              return (
                <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-3 active:bg-gray-50 transition-colors"
                  data-testid={`project-card-${p.id}`}>
                  <div className="flex gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate">{p.title || 'Untitled Project'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 ${status.color}`}>
                          <StatusIcon size={8} /> {status.label}
                        </span>
                        <span className="text-[10px] text-gray-400">{new Date(p.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-1">
                      <button onClick={() => navigate(`/video-studio/${p.product_id}`, { state: { projectId: p.id } })}
                        className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600" data-testid={`open-project-${p.id}`}>
                        <Play size={14} />
                      </button>
                      <button onClick={() => deleteProject(p.id)}
                        className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-500" data-testid={`delete-project-${p.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
